import { useCallback, useState } from "react"

/**
 * Confirmation gate for the Preferred Language selector. Mirrors the pattern used by
 * usePendingProfileTypeChange / useConfirmableEmailEdit: changes are intercepted into
 * a pending state instead of committing immediately, and the parent renders an inline
 * confirmation panel that calls confirm()/cancel() to commit or revert.
 *
 * The committed value lives on the form (currentValue), so this hook is purely for
 * holding the awaiting-confirmation candidate. Dropdown picks that match the current
 * value are no-ops — only an actual change requires confirmation.
 */
export interface UsePendingLanguageChangeProps {
  currentValue: string
  onCommit: (next: string) => void
}

export interface UsePendingLanguageChangeReturn {
  /** The value waiting for confirmation; null when no change is in flight. */
  pendingValue: string | null
  /** Called by the dropdown when the user picks an option. Sets pendingValue when the
   *  pick differs from currentValue; otherwise does nothing. */
  attemptChange: (next: string) => void
  /** Commit the pending value. No-op when nothing is pending. */
  confirm: () => void
  /** Discard the pending value, leaving currentValue untouched. */
  cancel: () => void
}

export function usePendingLanguageChange({
  currentValue,
  onCommit,
}: UsePendingLanguageChangeProps): UsePendingLanguageChangeReturn {
  const [pendingValue, setPendingValue] = useState<string | null>(null)

  const attemptChange = useCallback(
    (next: string) => {
      if (next !== currentValue) setPendingValue(next)
    },
    [currentValue],
  )

  const confirm = useCallback(() => {
    setPendingValue((pending) => {
      if (pending !== null) onCommit(pending)
      return null
    })
  }, [onCommit])

  const cancel = useCallback(() => setPendingValue(null), [])

  return { pendingValue, attemptChange, confirm, cancel }
}
